import { VERSION } from '@/lib/version';
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { CalendarDays, ClipboardList, FileText, LayoutDashboard, LogOut, Repeat, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { useSession } from '@/hooks/useSession';
import { type Espace, LIBELLE_ESPACE, espaceCourant, espacesDu, memoriserEspace, nomMembre, useMembre } from '@/hooks/useMembre';
import { CreerCabinet } from '@/features/auth/CreerCabinet';

const ESPACE_DES_ROUTES: [RegExp, Espace][] = [[/^\/assistant/, 'assistant'], [/^\/expert/, 'expert'], [/^\/admin/, 'admin']];
const COULEUR_ESPACE: Record<Espace, string> = { assistant: 'bg-violet-100 text-violet-800', expert: 'bg-sky-100 text-sky-800', admin: 'bg-amber-100 text-amber-900' };

export function AppShell() {
  const { session, chargement } = useSession();
  const membre = useMembre();
  const loc = useLocation();
  const naviguer = useNavigate();
  if (chargement || (session && membre.isLoading)) return <Skeleton className="m-6 h-40" />;
  if (!session) return <Navigate to="/" replace />;
  const m = membre.data;
  const espace = espaceCourant(m?.role);
  // l'URL fait foi quand elle désigne un espace autorisé
  const espaceUrl = ESPACE_DES_ROUTES.find(([re]) => re.test(loc.pathname))?.[1];
  if (m && espaceUrl && !espacesDu(m.role).includes(espaceUrl)) return <Navigate to={`/${espace}`} replace />;
  if (m && loc.pathname === '/espace') return <Navigate to={`/${espace}`} replace />;
  // l'espace désigné par l'URL devient l'espace courant (Paramètres, Contrats… s'y adaptent)
  if (m && espaceUrl && espaceUrl !== espace) memoriserEspace(espaceUrl);
  const espaceAffiche = espaceUrl ?? espace;
  const lien = ({ isActive }: { isActive: boolean }) =>
    cn('inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm', isActive ? 'bg-accent font-medium' : 'hover:bg-accent');
  const autres = m ? espacesDu(m.role).filter((e) => e !== espaceAffiche) : [];

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-[1600px] items-center gap-3 px-4">
          <Link to={`/${espaceAffiche}`} className="font-semibold">Les Experts <span className="text-xs font-normal text-muted-foreground">{VERSION}</span></Link>
          <span className={cn('rounded px-2 py-0.5 text-xs font-medium', COULEUR_ESPACE[espaceAffiche])}>{LIBELLE_ESPACE[espaceAffiche]}</span>
          <nav className="flex gap-1">
            {espaceAffiche === 'assistant' && <NavLink to="/assistant" end className={lien}><ClipboardList className="size-4" /> Qualification</NavLink>}
            {espaceAffiche === 'expert' && <NavLink to="/expert" end className={lien}><LayoutDashboard className="size-4" /> Mes dossiers</NavLink>}
            {espaceAffiche === 'admin' && <NavLink to="/admin" end className={lien}><ShieldCheck className="size-4" /> Administration</NavLink>}
            <NavLink to="/contrats" className={lien}><FileText className="size-4" /> Contrats</NavLink>
            {espaceAffiche !== 'admin' && <NavLink to="/parametres" className={lien}><CalendarDays className="size-4" /> Paramètres</NavLink>}
          </nav>
          <div className="ml-auto flex items-center gap-2 text-sm text-muted-foreground">
            {autres.map((e) => (
              <Button key={e} variant="outline" size="sm" onClick={() => { memoriserEspace(e); naviguer(`/${e}`); }}>
                <Repeat /> {e === 'admin' ? 'Administration' : `Espace ${LIBELLE_ESPACE[e]}`}
              </Button>
            ))}
            <span className="hidden sm:inline">{m ? nomMembre(m) : session.user.email}</span>
            <Button variant="ghost" size="icon" onClick={() => supabase.auth.signOut()} aria-label="Déconnexion"><LogOut /></Button>
          </div>
        </div>
      </header>
      {m ? <Outlet /> : <CreerCabinet email={session.user.email} />}
    </div>
  );
}
